import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { authApi } from "../services/api";
import { tokenStore, onSessionExpired } from "../services/authClient";

// User types
export const USER_TYPES = {
  CUSTOMER: "customer",
  BUSINESS_ADMIN: "business_owner",
  SUPER_ADMIN: "super_admin",
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tokens, setTokens] = useState({
    access_token: null,
    id_token: null,
    refresh_token: null,
  });
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage on mount.
  //
  // Deliberately does NOT check whether the access token has expired. A partner
  // coming back from Stripe's payout onboarding has been away longer than the
  // 60-minute token lifetime by design; treating that as "logged out" here would
  // bounce them to the login screen, which is the bug this is meant to fix.
  // Let the stale token through — authClient refreshes it on the first call.
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedAccessToken = tokenStore.getAccess();
        const storedUser = localStorage.getItem("user_profile");

        if (storedAccessToken && storedUser) {
          setTokens({
            access_token: storedAccessToken,
            id_token: tokenStore.getId(),
            refresh_token: tokenStore.getRefresh(),
          });
          setUser({
            ...JSON.parse(storedUser),
          });
          // Migration: a session that predates this feature (or the signup OTP
          // path that seeds tokens before login() runs) has no cap recorded. Seed
          // one fresh window so the countdown has a deadline; a key that is already
          // present is left untouched, so a reload preserves the real deadline.
          if (tokenStore.getSessionExpiry() == null) {
            tokenStore.beginSessionCap();
          }
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        tokenStore.clear();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // authClient clears storage and fires this when a refresh fails outright —
  // the refresh token itself is expired or revoked, so there is no way back
  // without signing in again.
  useEffect(() => {
    return onSessionExpired(() => {
      setUser(null);
      setTokens({ access_token: null, id_token: null, refresh_token: null });

      // A full navigation, not react-router: this fires from an axios
      // interceptor outside the router, and the destination should be a clean
      // reload anyway.
      const { pathname, origin } = window.location;
      if (pathname !== "/admin-login") {
        window.location.assign(`${origin}/admin-login?reason=session_expired`);
      }
    });
  }, []);

  // Login function
  const login = async (loginData, userType = USER_TYPES.CUSTOMER) => {
    try {
      const response = await authApi.login(loginData);

      if (response.access_token) {
        const userProfile = response.user_profile || {};

        const userData = {
          ...userProfile,
        };

        // Store tokens and user data
        tokenStore.setSession({
          access_token: response.access_token,
          id_token: response.id_token,
          refresh_token: response.refresh_token,
          user_profile: userProfile,
        });
        // Start the fixed 24h session cap from this login. This is the one place a
        // normal sign-in records its deadline; createPassword.js reaches here via
        // login() and inherits it.
        tokenStore.beginSessionCap();

        setTokens({
          access_token: response.access_token,
          id_token: response.id_token || null,
          refresh_token: response.refresh_token || null,
        });
        setUser(userData);

        return { success: true, data: response };
      } else {
        // This API answers a failed login with HTTP 200 and an { error } body, so the
        // failure arrives here rather than in catch. Throwing a fixed string discarded the
        // backend's reason: an unverified partner got "Login failed" instead of "User is
        // not confirmed", with nothing pointing them at /verify-email.
        return {
          success: false,
          error: response.error || "No access token received",
        };
      }
    } catch (error) {
      // authApi.login rejects with the parsed error body, or { message } for a transport
      // failure — `error.result` never existed, so this always fell through to "Login failed".
      console.log("Login error:", error);
      return {
        success: false,
        error: error?.error || error?.message || "Login failed",
      };
    }
  };

  // Logout function
  const logout = () => {
    // Capture before clearing: tokenStore.clear() wipes it, and the revoke needs
    // the refresh token. Clearing local state stays synchronous so logout feels
    // instant and callers don't have to await.
    const refresh_token = tokenStore.getRefresh();

    tokenStore.clear();
    setUser(null);
    setTokens({
      access_token: null,
      id_token: null,
      refresh_token: null,
    });

    // Revoke the session server-side so a held/stolen refresh token dies, not
    // just the local copy. RevokeToken operates on the refresh token alone, so
    // that's all we send. Best-effort and fire-and-forget: it survives the
    // client-side navigation that usually follows logout, and if it never lands
    // (offline, tab closed) the 1-day TTL + rotation bound the exposure. Never
    // let it block or fail the sign-out.
    if (refresh_token) {
      authApi.logout({ refresh_token }).catch(() => {});
    }
  };

  // Check if user is authenticated
  const isAuthenticated = useMemo(() => {
    return !!tokens.access_token && !!user;
  }, [tokens.access_token, user]);

  // Check user type helpers - using useMemo to make them reactive
  const isCustomer = useMemo(() => {
    return user?.profile_type === USER_TYPES.CUSTOMER;
  }, [user]);

  const isBusinessAdmin = useMemo(() => {
    return user?.profile_type === USER_TYPES.BUSINESS_ADMIN;
  }, [user]);

  const isSuperAdmin = useMemo(() => {
    return user?.profile_type === USER_TYPES.SUPER_ADMIN;
  }, [user]);

  // Get authorization header for API calls
  const getAuthHeader = () => {
    if (tokens.access_token) {
      return {
        Authorization: `Bearer ${tokens.access_token}`,
      };
    }
    return {};
  };

  const value = {
    user,
    tokens,
    loading,
    login,
    logout,
    isAuthenticated,
    isCustomer,
    isBusinessAdmin,
    isSuperAdmin,
    getAuthHeader,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
